/**
 * 重点游戏模块
 * @description 包含模块切换Segment和指标切换Segment，整合热门游戏和黑马游戏
 */
import React, { useRef, useMemo, useState, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Segment } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import ExportTable from '@intelligence/components/ExportTable';
import InfoIconTip from '@intelligence/components/InfoIconTip';
import { getAdjustedRevenueValue } from '@intelligence/utils/revenueScope';
import TopGames, { ITopGamesInstance } from '../TopGames';
import RisingGames, { IRisingGamesInstance } from '../RisingGames';
import { keyGamesEnableConfigs } from '../../services';
import { dbhBtnLogMes } from '@home/utils/buriedPointCfg';
import { isEmpty } from '@intelligence/v3/utils/common';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { DEFAULT_KEY_GAMES_SORT_STATE, filterRevenueMetrics, getSortOrderBys } from '../../utils';
import { ChartType } from '@intelligence/v3/enums/chart';
import {
  DEFAULT_MODULE_TAB,
  ENTITY_TYPE,
  KEY_GAMES_TAB_OPTIONS,
  MODULE_TAB_OPTIONS,
  getDataSourceMetrics,
} from '../../constants';

import type { ICasualCompetitorsDataSourceProps, ICasualCompetitorsParams } from '../../types';

import './index.scss';

type TKeyGamesModuleTab = 'top_games' | 'rising_games';
type TKeyGamesSortStateMap = Record<TKeyGamesModuleTab, IRatioMetricSortState<IRecord>>;
type TKeyGamesPageGameIdsMap = Record<TKeyGamesModuleTab, string[]>;

const KEY_GAMES_MODULE_TIP_GROUP = 'casual-competitors-key-games-module-tip';

/**
 * 模块实例接口
 */
export interface IKeyGamesInstance {
  update: (params: ICasualCompetitorsParams) => void;
}

interface IKeyGamesProps extends IBaseProps, ICasualCompetitorsDataSourceProps {
  keyGamesTableTipsI18n?: IRecord<IRecord<string>>;
  params: ICasualCompetitorsParams | null;
  genreOptionsMap?: IRecord<any[]>;
}

// 默认指标
const DEFAULT_METRIC_TAB = 'daily_avg_dau';

// 指标列表
const KEY_GAMES_METRIC_NAMES = ['daily_avg_dau', 'iap_revenue', 'download', 'dau_share'];

const keyGamesDownloadCfg = dbhBtnLogMes.studioCasualHubCompetitorsKeyGamesDownload;

const createDefaultSortState = (dataSource: string): IRatioMetricSortState<IRecord> => {
  return {
    ...DEFAULT_KEY_GAMES_SORT_STATE,
    dataSource,
  };
};

const createDefaultSortStateMap = (dataSource: string): TKeyGamesSortStateMap => {
  return {
    top_games: createDefaultSortState(dataSource),
    rising_games: createDefaultSortState(dataSource),
  };
};

const createDefaultPageGameIdsMap = (): TKeyGamesPageGameIdsMap => {
  return {
    top_games: [],
    rising_games: [],
  };
};

const chartOperations = [
  {
    icon: 'column-chart',
    symbol: ChartType.Bar,
  },
  {
    icon: 'discount-chart',
    symbol: ChartType.Line,
  },
  {
    icon: 'pie-chart-icon',
    symbol: ChartType.Pie,
  },
];

const getModuleSegmentOptionBubbleI18nKey = (
  bubbleI18nKey?: string,
  granularity?: string,
  genreId?: string,
): string => {
  if (isEmpty(bubbleI18nKey)) {
    return '';
  }

  if (bubbleI18nKey === 'dbh_studio_casual_rising_games_bubble') {
    return `${bubbleI18nKey}_${granularity}_${genreId}`;
  }

  // @ts-ignore
  return bubbleI18nKey;
};

const KeyGames: React.ForwardRefRenderFunction<IKeyGamesInstance, IKeyGamesProps> = (props, ref) => {
  const { style, className, keyGamesTableTipsI18n, params, genreOptionsMap, dataSource } = props;
  const { formatMessage } = useIntl();

  // 模块切换状态
  const [moduleTab, setModuleTab] = useState<TKeyGamesModuleTab>(DEFAULT_MODULE_TAB as TKeyGamesModuleTab);

  // 指标切换状态
  const [metricTab, setMetricTab] = useState(DEFAULT_METRIC_TAB);

  // 下载使用的最新排序状态
  const [latestSortStateMap, setLatestSortStateMap] = useState<TKeyGamesSortStateMap>(() => {
    return createDefaultSortStateMap(dataSource);
  });
  const [latestPageGameIdsMap, setLatestPageGameIdsMap] =
    useState<TKeyGamesPageGameIdsMap>(createDefaultPageGameIdsMap);

  // 图表类型
  const [chartType, setChartType] = useState<ChartType>(ChartType.Line);

  // 子模块实例
  const topGamesRef = useRef<ITopGamesInstance>(null);
  const risingGamesRef = useRef<IRisingGamesInstance>(null);

  // 缓存最新的 params，用于模块切换时重新加载
  const latestParamsRef = useRef<ICasualCompetitorsParams | null>(null);

  // 从 props 获取 genreId
  const genreId = params?.genreId || '';
  // 从 props 获取 showRevenue
  const showRevenue = params?.showRevenue ?? true;
  const keyGamesMetrics = useMemo(() => {
    return getDataSourceMetrics(dataSource, KEY_GAMES_METRIC_NAMES);
  }, [dataSource]);

  const updateLatestSortState = (tab: TKeyGamesModuleTab, sortState: IRatioMetricSortState<IRecord>) => {
    setLatestSortStateMap((prev) => {
      return {
        ...prev,
        [tab]: { ...sortState },
      };
    });
  };

  const resetLatestSortState = (tab: TKeyGamesModuleTab) => {
    updateLatestSortState(tab, createDefaultSortState(dataSource));
  };

  const updateLatestPageGameIds = (tab: TKeyGamesModuleTab, gameIds: string[]) => {
    setLatestPageGameIdsMap((prev) => {
      return {
        ...prev,
        [tab]: gameIds,
      };
    });
  };

  const resetLatestPageGameIds = (tab: TKeyGamesModuleTab) => {
    updateLatestPageGameIds(tab, []);
  };

  /**
   * 模块切换处理
   */
  const handleModuleTabChange = (value: string) => {
    const nextModuleTab = value as TKeyGamesModuleTab;
    setModuleTab(nextModuleTab);
    // 切换模块时重置指标为第一个
    setMetricTab(DEFAULT_METRIC_TAB);
    resetLatestSortState(nextModuleTab);
    resetLatestPageGameIds(nextModuleTab);

    // 这里需要等对应模块渲染出来后，再调用模块实例的update方法加载数据
    setTimeout(() => {
      // 切换模块时重新加载对应模块数据
      if (latestParamsRef.current) {
        if (nextModuleTab === 'top_games') {
          topGamesRef.current?.update(latestParamsRef.current);
        } else {
          risingGamesRef.current?.update(latestParamsRef.current);
        }
      }
    });
  };

  /**
   * 指标切换处理
   */
  const handleMetricTabChange = (value: string) => {
    setMetricTab(value);
  };

  /**
   * 暴露 update 方法
   */
  useImperativeHandle(ref, () => ({
    update: (params: ICasualCompetitorsParams) => {
      latestParamsRef.current = params;

      // IAA Arrows无黑马游戏数据，禁用了黑马游戏Segment，如果当前选中的是黑马游戏Segment需要自动切换为第一个Segment
      let newModuleTab = moduleTab;
      if (params?.genreId === 'iaa_arrows') {
        if (moduleTab === 'rising_games') {
          newModuleTab = DEFAULT_MODULE_TAB;
          setModuleTab(newModuleTab);
        }
      }

      // IAA 品类无收入指标，重置为默认
      if (!params.showRevenue && metricTab === 'iap_revenue') {
        setMetricTab(DEFAULT_METRIC_TAB);
      }

      // 只加载当前选中模块的数据
      resetLatestSortState(newModuleTab);
      resetLatestPageGameIds(newModuleTab);
      if (newModuleTab === 'top_games') {
        topGamesRef.current?.update(params);
      } else {
        risingGamesRef.current?.update(params);
      }
    },
  }));

  /**
   * 过滤指标选项
   */
  const filteredMetricOptions = useMemo(() => {
    return KEY_GAMES_TAB_OPTIONS.filter((opt) => {
      return !(opt.value === 'iap_revenue' && !showRevenue);
    });
  }, [showRevenue]);

  /**
   * 下载配置
   */
  const downloadConfig = useMemo(() => {
    if (moduleTab === 'top_games') {
      return {
        businessType: 'casualCompetitorsKeyGames',
        downloadKey: 'casualCompetitorsKeyGames',
        downloadLogConfig: { ...keyGamesDownloadCfg, extInfo: `${keyGamesDownloadCfg.extInfo}(Top Games)` },
        newStyleFileName: 'Competitors_Top_Games',
      };
    }
    return {
      businessType: 'casualCompetitorsKeyGames',
      downloadKey: 'casualCompetitorsKeyGames',
      downloadLogConfig: { ...keyGamesDownloadCfg, extInfo: `${keyGamesDownloadCfg.extInfo}(Rising Games)` },
      newStyleFileName: 'Competitors_Rising_Games',
    };
  }, [moduleTab]);

  /**
   * 获取下载请求参数
   */
  const getDownloadReqParams = () => {
    if (!params) {
      return {};
    }

    const metrics = filterRevenueMetrics(keyGamesMetrics, showRevenue);
    const enableConfig = keyGamesEnableConfigs[moduleTab];
    const currentSortState = latestSortStateMap[moduleTab] || createDefaultSortState(dataSource);
    const orderBys = getSortOrderBys(currentSortState, dataSource);
    const pageGameIds = latestPageGameIdsMap[moduleTab] || [];
    const adjustedRevenueFilter = (() => {
      const revenueScope = params?.revenueScope;

      if (isEmpty(revenueScope)) {
        return {};
      }

      return {
        adjusted_revenue: getAdjustedRevenueValue(revenueScope),
      };
    })();

    return {
      hasRevenue: showRevenue,
      metrics,
      markets: params.markets,
      platforms: params.platforms,
      filter: {
        ...adjustedRevenueFilter,
        ...enableConfig,
        genre_id: genreId,
      },
      order_by: orderBys,
      page_game_ids: pageGameIds,
      granularity: params.granularity,
      start_date: params.startDate,
      end_date: params.endDate,
    };
  };

  const currentPageGameIds = latestPageGameIdsMap[moduleTab] || [];

  return (
    <CardCommon
      style={style}
      className={classnames('dbh-card-table', className)}
      title={formatMessage({ id: 'dbh_studio_casual_key_games_title' })}
      // @ts-ignore
      operationInfo={[chartType, setChartType]}
      operations={[
        ...chartOperations,
        {
          symbol: 'download',
          element: (
            <ExportTable
              useWebDownload
              downloadModalSize={800}
              entityType={ENTITY_TYPE}
              businessType={downloadConfig.businessType}
              downloadKey={downloadConfig.downloadKey}
              downloadLogConfig={downloadConfig.downloadLogConfig}
              newStyleFileName={downloadConfig.newStyleFileName}
              stickText={formatMessage({
                id: 'dbi_mobile_download_modal_stick_tips',
              })}
              total={MAX_ROWS_LIMIT}
              isDisableDownload={!params || isEmpty(currentPageGameIds)}
              tableParams={getDownloadReqParams()}
            />
          ),
        },
      ]}
      handleOperationsOnClick={(item) => {
        if (item.symbol) {
          setChartType(item.symbol as ChartType);
        }
      }}
    >
      {/* Segment 行：模块切换 + 指标切换 */}
      <div className="dbh-panel-key-games-segment-row">
        {/* 模块切换 Segment */}
        <Segment
          options={MODULE_TAB_OPTIONS.map((opt) => {
            const optValue = opt?.value || '';
            const isSelected = moduleTab === optValue;
            const tipText = formatMessage({
              id: getModuleSegmentOptionBubbleI18nKey(opt.bubble, params?.granularity, genreId),
            });

            return {
              disabled: optValue === 'rising_games' && genreId === 'iaa_arrows',
              text: (
                <span className="dbh-panel-key-games-module-tab">
                  <span>{formatMessage({ id: opt.text })}</span>
                  <InfoIconTip
                    tipText={tipText}
                    selected={isSelected}
                    // @ts-ignore
                    placement={opt.bubblePlacement || 'top'}
                    groupId={KEY_GAMES_MODULE_TIP_GROUP}
                    className="dbh-panel-key-games-module-tab__tip"
                  />
                </span>
              ),
              value: optValue,
            };
          })}
          value={moduleTab}
          onChange={handleModuleTabChange}
        />
        <span className="dbh-panel-key-games-segment-divider">-</span>
        {/* 指标切换 Segment */}
        <Segment
          options={filteredMetricOptions.map((opt) => ({
            text: formatMessage({ id: opt.text }),
            value: opt.value,
          }))}
          value={metricTab}
          onChange={handleMetricTabChange}
        />
      </div>

      {/* 热门游戏模块 - 使用 CSS 控制显示隐藏，保持组件挂载 */}
      {moduleTab === 'top_games' && (
        <TopGames
          ref={topGamesRef}
          keyGamesTableTipsI18n={keyGamesTableTipsI18n}
          params={params}
          dataSource={dataSource}
          genreOptionsMap={genreOptionsMap}
          metricTab={metricTab}
          onMetricTabChange={setMetricTab}
          chartType={chartType}
          onSortStateChange={(sortState) => {
            updateLatestSortState('top_games', sortState);
          }}
          onSortedGameIdsChange={(gameIds) => {
            updateLatestPageGameIds('top_games', gameIds);
          }}
        />
      )}

      {/* 黑马游戏模块 - 使用 CSS 控制显示隐藏，保持组件挂载 */}
      {moduleTab === 'rising_games' && (
        <RisingGames
          ref={risingGamesRef}
          keyGamesTableTipsI18n={keyGamesTableTipsI18n}
          params={params}
          dataSource={dataSource}
          genreOptionsMap={genreOptionsMap}
          metricTab={metricTab}
          onMetricTabChange={setMetricTab}
          chartType={chartType}
          onSortStateChange={(sortState) => {
            updateLatestSortState('rising_games', sortState);
          }}
          onSortedGameIdsChange={(gameIds) => {
            updateLatestPageGameIds('rising_games', gameIds);
          }}
        />
      )}
    </CardCommon>
  );
};

// @ts-ignore
export default React.forwardRef(KeyGames);
